/**
 * Datasets - os semantic models do Power BI, onde moram os dados e as roles de RLS.
 *
 * A tela consulta TAMBEM as conexoes, por dois motivos: mostrar o nome da conexao em vez do GUID,
 * e oferecer na edicao so as conexoes DO MESMO CLIENTE - o backend recusa
 * `powerbi_connection_client_mismatch`, e uma lista que deixa escolher o que vai ser recusado nao
 * ajuda ninguem.
 */

import { useQuery } from '@tanstack/react-query'

import { fetchConnections, fetchDatasets } from '../api/catalog'
import { RESOURCE_CATALOGO } from '../api/types'
import type { Dataset, PowerBiConnection } from '../api/types'
import { usePermissoes } from '../auth/permissoes'
import type { CampoMeta } from '../listagem/campos'
import { IdCurto, Listagem, Marca } from '../listagem/Listagem'
import { Resultado } from '../listagem/Resultado'

function campos(conexoes: PowerBiConnection[]): Array<CampoMeta<Dataset>> {
  const nome = new Map(conexoes.map((item) => [item.id, item.name]))
  return [
    {
      nome: 'name',
      rotulo: 'Nome',
      grupo: 'Identificação',
      edicao: { tipo: 'texto', dica: 'Rótulo livre, para identificação.' },
    },
    {
      nome: 'description',
      rotulo: 'Descrição',
      grupo: 'Identificação',
      edicao: { tipo: 'textarea', opcional: true },
    },
    { nome: 'client_slug', rotulo: 'Cliente', grupo: 'Identificação', formato: 'codigo' },
    {
      nome: 'powerbi_connection_id',
      rotulo: 'Conexão Power BI',
      grupo: 'Power BI',
      exibir: (item) =>
        item.powerbi_connection_id === null ? (
          <span className="vazio">—</span>
        ) : (
          (nome.get(item.powerbi_connection_id) ?? <IdCurto valor={item.powerbi_connection_id} />)
        ),
      nota: 'Define com qual identidade do Entra este dataset é consultado.',
      edicao: {
        tipo: 'selecao',
        dica: 'Só as conexões do mesmo cliente — o backend recusa as de outro.',
        opcoes: (item) =>
          conexoes
            .filter((conexao) => conexao.client_id === item.client_id)
            .map((conexao) => ({ valor: conexao.id, rotulo: conexao.name })),
      },
    },
    {
      nome: 'powerbi_workspace_id',
      rotulo: 'Workspace no Power BI (groupId)',
      grupo: 'Power BI',
      formato: 'identificador',
      edicao: { tipo: 'texto', dica: 'Sai do link do relatório, entre /groups/ e /reports/.' },
    },
    {
      nome: 'powerbi_dataset_id',
      rotulo: 'Dataset no Power BI',
      grupo: 'Power BI',
      formato: 'identificador',
      nota: 'É este valor que vai no token de embed e decide de onde vêm os dados.',
      edicao: { tipo: 'texto', dica: 'GUID do semantic model, nas configurações do dataset.' },
    },
    {
      nome: 'is_active',
      rotulo: 'Dataset ativo',
      grupo: 'Estado',
      formato: 'booleano',
      edicao: { tipo: 'booleano' },
    },
    { nome: 'client_id', rotulo: 'Cliente (identificador)', grupo: 'Registro', formato: 'identificador' },
    { nome: 'id', rotulo: 'Identificador no catálogo', grupo: 'Registro', formato: 'identificador' },
  ]
}

export function DatasetsPage() {
  const { pode } = usePermissoes()
  const datasets = useQuery({ queryKey: ['datasets'], queryFn: fetchDatasets })
  const conexoes = useQuery({ queryKey: ['powerbi-connections'], queryFn: fetchConnections })
  const lista = conexoes.data ?? []
  const nomeDaConexao = new Map(lista.map((item) => [item.id, item.name]))

  return (
    <>
      <Resultado consulta={datasets}>
        {(itens) => (
          <Listagem<Dataset>
            itens={itens}
            chave="datasets"
            entidade="datasets"
            rotulo="dataset"
            campos={campos(lista)}
            podeGerenciar={pode(RESOURCE_CATALOGO, 'manage')}
            idDe={(item) => item.id}
            nomeDe={(item) => item.name}
            colunas={[
              { titulo: 'Nome', valor: (item) => item.name },
              { titulo: 'Cliente', valor: (item) => item.client_slug },
              {
                titulo: 'Conexão',
                valor: (item) =>
                  item.powerbi_connection_id === null
                    ? '—'
                    : (nomeDaConexao.get(item.powerbi_connection_id) ?? (
                        <IdCurto valor={item.powerbi_connection_id} />
                      )),
              },
              {
                titulo: 'Dataset no Power BI',
                valor: (item) => <IdCurto valor={item.powerbi_dataset_id} />,
              },
              { titulo: 'Ativo', valor: (item) => <Marca valor={item.is_active} /> },
            ]}
            avisoDeEdicao="Se este dataset já tem relatórios, os ids do Power BI não podem ser trocados: o embed deles aponta para esses ids."
            vazio="Nenhum dataset cadastrado. O cadastro é feito no onboarding, passo 4."
          />
        )}
      </Resultado>
    </>
  )
}
